import React, { useState, useEffect } from 'react';
import { 
  Card, 
  Table, 
  Collapse, 
  Space, 
  Tag, 
  Typography, 
  Statistic,
  Row,
  Col,
  Empty,
  Radio,
  Select,
  DatePicker,
  Divider
} from 'antd';
import dayjs from 'dayjs';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, AreaChart, Area } from 'recharts';
import { 
  CalendarOutlined, 
  ShoppingOutlined, 
  DollarOutlined,
  FolderOutlined
} from '@ant-design/icons';
import { database, ref, get } from '../../pages/Auth/db/firebase';
import { getFacilityCurrency, formatPriceWithCurrency, preloadCustomCurrencies } from '../../utils/currency';

const { Panel } = Collapse;
const { Text } = Typography;

const TransactionHistory = ({ facilityId }) => {
  const [transactions, setTransactions] = useState({});
  const [loading, setLoading] = useState(false);
  const [viewMode, setViewMode] = useState('table');
  const [facilityCurrency, setFacilityCurrency] = useState('USD');
  const [periodFilter, setPeriodFilter] = useState('all'); // 'all', 'year', 'month', 'week', 'day', 'range'
  const [selectedPeriod, setSelectedPeriod] = useState(null); // For specific period selection
  const [dateRange, setDateRange] = useState(null); // For date range selection [start, end]
  const [chartView, setChartView] = useState('separate'); // 'separate', 'sales-profit', 'transactions-items'
  const [selectedChartCurrency, setSelectedChartCurrency] = useState(null); // Currency for charts, null = facilityCurrency

  useEffect(() => {
    loadTransactions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [facilityId]);

  // Load facility currency and preload custom currencies
  useEffect(() => {
    if (!facilityId) return;
    
    const loadCurrency = async () => {
      try {
        const currency = await getFacilityCurrency(facilityId);
        setFacilityCurrency(currency);
        // Set chart currency to facility currency by default
        setSelectedChartCurrency(currency);
        // Preload custom currencies for proper formatting
        await preloadCustomCurrencies(facilityId);
      } catch (error) {
        console.error('Error loading currency:', error);
      }
    };
    
    loadCurrency();
  }, [facilityId]);

  const loadTransactions = async () => {
    if (!facilityId) return;
    
    setLoading(true);
    try {
      const transactionsRef = ref(database, `transactions/${facilityId}`);
      const snapshot = await get(transactionsRef);
      
      if (snapshot.exists()) {
        const data = snapshot.val();
        // Handle both data structures: 
        // 1. transactions/{facilityId}/{date}/{transactionId} (CashierMode)
        // 2. transactions/{facilityId}/{transactionId} (CheckoutInterface)
        
        const processedTransactions = {};
        
        Object.keys(data).forEach(key => {
          const item = data[key];

          // Date bucket present (YYYY-MM-DD)
          if (/^\d{4}-\d{2}-\d{2}$/.test(key)) {
            processedTransactions[key] = item || {};
            return;
          }

          // Flat transaction node: derive date bucket
          if (item && typeof item === 'object') {
            let transactionDate = null;
            if (item.timestamp) {
              const d = new Date(item.timestamp);
              if (!isNaN(d.getTime())) transactionDate = d.toISOString().split('T')[0];
            }
            if (!transactionDate && item.date && /^\d{4}-\d{2}-\d{2}$/.test(item.date)) {
              transactionDate = item.date;
            }
            if (!transactionDate && !Number.isNaN(Number(key))) {
              const d = new Date(Number(key));
              if (!isNaN(d.getTime())) transactionDate = d.toISOString().split('T')[0];
            }
            if (!transactionDate) {
              // Fallback to today to ensure visibility
              transactionDate = new Date().toISOString().split('T')[0];
            }
            if (!processedTransactions[transactionDate]) processedTransactions[transactionDate] = {};
            processedTransactions[transactionDate][key] = item;
          }
        });
        
        setTransactions(processedTransactions);
      } else {
        setTransactions({});
      }
    } catch (error) {
      console.error('Failed to load transactions:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown Date';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return dateString; // fallback to raw key like YYYY-MM-DD
      return date.toLocaleDateString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch (e) {
      return dateString;
    }
  };

  const calculateDayStats = (dayTransactions) => {
    if (!dayTransactions || typeof dayTransactions !== 'object') {
      return { transactionCount: 0, totalSales: 0, totalItems: 0, totalProfit: 0, totalsByCurrency: {}, profitsByCurrency: {} };
    }
    
    const transactionArray = Object.values(dayTransactions).filter(t => t && typeof t === 'object');
    
    // Calculate totals by currency
    const totalsByCurrency = {};
    const profitsByCurrency = {};
    
    transactionArray.forEach(transaction => {
      const items = Array.isArray(transaction.items) ? transaction.items : [];
      
      // Calculate from items if available
      if (items.length > 0) {
        items.forEach(item => {
          const itemCurrency = item?.currency || facilityCurrency;
          const itemPrice = Number(item?.price) || 0; // Selling price
          const itemQuantity = Number(item?.quantity) || 0;
          const purchasingPrice = Number(item?.purchasingPrice) || 0;
          
          const subtotal = itemPrice * itemQuantity;
          const costTotal = purchasingPrice * itemQuantity;
          const profit = subtotal - costTotal;
          
          if (!totalsByCurrency[itemCurrency]) {
            totalsByCurrency[itemCurrency] = 0;
            profitsByCurrency[itemCurrency] = 0;
          }
          totalsByCurrency[itemCurrency] += subtotal;
          profitsByCurrency[itemCurrency] += profit;
        });
      } else {
        // Fallback to transaction total if items not available
      const total = parseFloat(transaction.total) || 0;
        const transactionCurrency = transaction?.currency || facilityCurrency;
        
        if (!totalsByCurrency[transactionCurrency]) {
          totalsByCurrency[transactionCurrency] = 0;
          profitsByCurrency[transactionCurrency] = 0;
        }
        totalsByCurrency[transactionCurrency] += total;
        // Can't calculate profit without item details
      }
    });
    
    // For backward compatibility, calculate total in facility currency
    const totalSales = totalsByCurrency[facilityCurrency] || 0;
    const totalProfit = profitsByCurrency[facilityCurrency] || 0;
    
    const totalItems = transactionArray.reduce((sum, transaction) => {
      const items = Array.isArray(transaction.items) ? transaction.items : [];
      return sum + items.reduce((itemSum, item) => {
        const quantity = parseInt(item?.quantity) || 0;
        return itemSum + quantity;
      }, 0);
    }, 0);
    
    return {
      transactionCount: transactionArray.length,
      totalSales,
      totalItems,
      totalProfit,
      totalsByCurrency,
      profitsByCurrency
    };
  };

  const transactionColumns = [
    {
      title: 'Time',
      dataIndex: 'timestamp',
      key: 'timestamp',
      render: (timestamp) => {
        if (!timestamp) return 'Unknown';
        try {
          let d = new Date(timestamp);
          if (isNaN(d.getTime())) {
            const num = Number(timestamp);
            if (!Number.isNaN(num)) d = new Date(num);
          }
          if (isNaN(d.getTime())) return 'Invalid Time';
          return d.toLocaleTimeString();
        } catch (e) {
          return 'Invalid Time';
        }
      },
      sorter: (a, b) => {
        const parse = (t) => {
          if (!t) return 0;
          let d = new Date(t);
          if (isNaN(d.getTime())) {
            const num = Number(t);
            if (!Number.isNaN(num)) d = new Date(num);
          }
          return d.getTime() || 0;
        };
        return parse(a.timestamp) - parse(b.timestamp);
      },
      defaultSortOrder: 'descend',
      width: 100,
    },
    {
      title: 'Items',
      dataIndex: 'items',
      key: 'items',
      render: (items, record) => {
        const itemsArray = Array.isArray(items) ? items : [];
        return (
          <Space direction="vertical" size="small">
            {itemsArray.map((item, index) => {
              const itemPrice = Number(item?.price) || 0;
              const itemCurrency = item?.currency || facilityCurrency;
              return (
                <Text key={index} style={{ fontSize: '12px', color: 'var(--lcolor)' }}>
                  {Number(item?.quantity) || 0}x {item?.name || 'Unknown'} @ {formatPriceWithCurrency(itemPrice, itemCurrency, facilityId)}
              </Text>
              );
            })}
          </Space>
        );
      },
    },
    {
      title: 'Total',
      dataIndex: 'total',
      key: 'total',
      render: (total, record) => {
        const items = Array.isArray(record?.items) ? record.items : [];

        const totalsByCurrency = {};
        const profitsByCurrency = {};

        if (items.length > 0) {
          items.forEach(item => {
            const itemCurrency = item?.currency || facilityCurrency;
            const itemPrice = Number(item?.price) || 0; // This is the selling price
            const itemQuantity = Number(item?.quantity) || 0;
            
            // Get purchasing price from item data (need to fetch from inventory)
            // For now, we'll calculate profit if purchasingPrice is available in the transaction
            const purchasingPrice = Number(item?.purchasingPrice) || 0;
            
            const subtotal = itemPrice * itemQuantity;
            const costTotal = purchasingPrice * itemQuantity;
            const profit = subtotal - costTotal;
            
            if (!totalsByCurrency[itemCurrency]) {
              totalsByCurrency[itemCurrency] = 0;
              profitsByCurrency[itemCurrency] = 0;
            }
            totalsByCurrency[itemCurrency] += subtotal;
            profitsByCurrency[itemCurrency] += profit;
          });
        } else {
          // Fallback to transaction total if items not available
        const totalAmount = parseFloat(total) || 0;
          const transactionCurrency = record?.currency || facilityCurrency;
          totalsByCurrency[transactionCurrency] = totalAmount;
          // Can't calculate profit without item details
          profitsByCurrency[transactionCurrency] = 0;
        }

        const currencies = Object.keys(totalsByCurrency);
        // If multiple currencies, show all
        if (currencies.length > 1) {
        return (
          <Space direction="vertical" size="small">
              {currencies.map(currency => (
                <div key={currency}>
                  <Text style={{color: 'var(--lcolor)', fontSize: '12px'}}>{formatPriceWithCurrency(totalsByCurrency[currency], currency, facilityId)}</Text>
                  {profitsByCurrency[currency] > 0 && (
                    <Text type="success" style={{ fontSize: '10px', display: 'block', color: 'var(--success-color)' }}>
                      Profit: {formatPriceWithCurrency(profitsByCurrency[currency], currency, facilityId)}
                    </Text>
                  )}
                </div>
              ))}
            </Space>
          );
        }
        
        // Single currency
        const singleCurrency = currencies[0] || facilityCurrency;
        const singleTotal = totalsByCurrency[singleCurrency] || 0;
        const singleProfit = profitsByCurrency[singleCurrency] || 0;
        
        return (
          <Space direction="vertical" size="small">
            <Text style={{color: 'var(--lcolor)'}} strong >{formatPriceWithCurrency(singleTotal, singleCurrency, facilityId)}</Text>
            {singleProfit > 0 && (
              <Text type="success" style={{ fontSize: '11px', color: 'var(--success-color)' }}>
                Profit: {formatPriceWithCurrency(singleProfit, singleCurrency, facilityId)}
              </Text>
            )}
          </Space>
        );
      },
      width: 120,
    },
    {
      title: 'Cashier',
      dataIndex: 'cashier',
      key: 'cashier',
      render: (cashier) => cashier || 'Unknown',
      width: 80,
    },
  ];

  const getTodaysTransactions = () => {
    const today = new Date().toISOString().split('T')[0];
    return transactions[today] || {};
  };

  const getHistoricalTransactions = () => {
    const today = new Date().toISOString().split('T')[0];
    const historical = { ...transactions };
    delete historical[today];
    return historical;
  };

  // Filter transactions by period
  const filterTransactionsByPeriod = (transactionsObj, period, selectedDate = null, dateRange = null) => {
    if (period === 'all') return transactionsObj;
    
    const now = new Date();
    const filtered = {};
    
    Object.keys(transactionsObj).forEach(dateStr => {
      const transactionDate = new Date(dateStr);
      if (isNaN(transactionDate.getTime())) return;
      
      let include = false;
      
      if (period === 'range' && dateRange && dateRange.length === 2) {
        const startDate = dayjs(dateRange[0]).startOf('day');
        const endDate = dayjs(dateRange[1]).endOf('day');
        const transDate = dayjs(dateStr);
        include = (transDate.isAfter(startDate.subtract(1, 'day')) || transDate.isSame(startDate, 'day')) && 
                  (transDate.isBefore(endDate.add(1, 'day')) || transDate.isSame(endDate, 'day'));
      } else if (period === 'year') {
        if (selectedDate) {
          const selected = new Date(selectedDate);
          include = transactionDate.getFullYear() === selected.getFullYear();
        } else {
          include = transactionDate.getFullYear() === now.getFullYear();
        }
      } else if (period === 'month') {
        if (selectedDate) {
          const selected = new Date(selectedDate);
          include = transactionDate.getFullYear() === selected.getFullYear() &&
                    transactionDate.getMonth() === selected.getMonth();
        } else {
          include = transactionDate.getFullYear() === now.getFullYear() &&
                    transactionDate.getMonth() === now.getMonth();
        }
      } else if (period === 'week') {
        const weekAgo = new Date(now);
        weekAgo.setDate(weekAgo.getDate() - 7);
        include = transactionDate >= weekAgo && transactionDate <= now;
      } else if (period === 'day') {
        if (selectedDate) {
          include = dateStr === selectedDate;
        } else {
          const today = now.toISOString().split('T')[0];
          include = dateStr === today;
        }
      }
      
      if (include) {
        filtered[dateStr] = transactionsObj[dateStr];
      }
    });
    
    return filtered;
  };

  // Calculate overall stats for filtered transactions
  const calculateOverallStats = (transactionsObj) => {
    let totalTransactions = 0;
    let totalItems = 0;
    const totalsByCurrency = {};
    const profitsByCurrency = {};
    
    Object.values(transactionsObj).forEach(dayTransactions => {
      const dayStats = calculateDayStats(dayTransactions);
      totalTransactions += dayStats.transactionCount;
      totalItems += dayStats.totalItems;
      
      Object.keys(dayStats.totalsByCurrency || {}).forEach(currency => {
        if (!totalsByCurrency[currency]) {
          totalsByCurrency[currency] = 0;
          profitsByCurrency[currency] = 0;
        }
        totalsByCurrency[currency] += dayStats.totalsByCurrency[currency];
        profitsByCurrency[currency] += dayStats.profitsByCurrency[currency] || 0;
      });
    });
    
    const totalSales = totalsByCurrency[facilityCurrency] || 0;
    const totalProfit = profitsByCurrency[facilityCurrency] || 0;
    
    return {
      totalTransactions,
      totalItems,
      totalSales,
      totalProfit,
      totalsByCurrency,
      profitsByCurrency
    };
  };

  // Prepare chart data (daily breakdown)
  const prepareChartData = (transactionsObj, currencyForChart = null) => {
    const chartData = [];
    const sortedDates = Object.keys(transactionsObj).sort((a, b) => new Date(a) - new Date(b));
    const targetCurrency = currencyForChart || facilityCurrency;
    
    sortedDates.forEach(date => {
      const dayStats = calculateDayStats(transactionsObj[date]);
      // Use the selected currency from multi-currency data, fallback to facility currency
      const sales = dayStats.totalsByCurrency?.[targetCurrency] || dayStats.totalSales || 0;
      const profit = dayStats.profitsByCurrency?.[targetCurrency] || dayStats.totalProfit || 0;
      
      chartData.push({
        date: formatDate(date),
        dateKey: date,
        transactions: dayStats.transactionCount,
        sales: sales,
        items: dayStats.totalItems,
        profit: profit,
        // Multi-currency support (keep for currency selector)
        salesByCurrency: dayStats.totalsByCurrency,
        profitByCurrency: dayStats.profitsByCurrency
      });
    });
    
    return chartData;
  };

  // Get all available currencies from chart data
  const getAvailableCurrencies = () => {
    const currencies = new Set([facilityCurrency]);
    chartData.forEach(item => {
      if (item.salesByCurrency) {
        Object.keys(item.salesByCurrency).forEach(currency => currencies.add(currency));
      }
      if (item.profitByCurrency) {
        Object.keys(item.profitByCurrency).forEach(currency => currencies.add(currency));
      }
    });
    return Array.from(currencies).sort();
  };

  const todaysTransactions = getTodaysTransactions();
  const allHistoricalTransactions = getHistoricalTransactions();
  const filteredHistoricalTransactions = filterTransactionsByPeriod(
    allHistoricalTransactions, 
    periodFilter, 
    selectedPeriod,
    dateRange
  );
  const todaysStats = Object.keys(todaysTransactions).length > 0 ? calculateDayStats(todaysTransactions) : null;
  const overallStats = calculateOverallStats(filteredHistoricalTransactions);
  // Prepare chart data with selected currency (defaults to facilityCurrency)
  const chartData = prepareChartData(filteredHistoricalTransactions, selectedChartCurrency || facilityCurrency);
  const availableCurrencies = getAvailableCurrencies();

  return (
    <div>
      {/* Today's Summary */}
      {todaysStats && (
        <Card 
          title={
            <span style={{ fontSize: '18px', fontWeight: 600, color: 'var(--lcolor)' }}>
              Today's Summary
            </span>
          }
          style={{ 
            marginBottom: 'var(--spacing-lg)',
            background: 'linear-gradient(135deg, var(--surface-primary) 0%, var(--surface-secondary) 100%)',
            border: '1px solid var(--border-tertiary)',
            boxShadow: 'var(--shadow-card)'
          }}
        >
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12} lg={6}>
              <Card 
                size="small"
                style={{ 
                  background: 'var(--surface-elevated)',
                  border: '1px solid var(--border-tertiary)',
                  boxShadow: 'var(--shadow-light)'
                }}
              >
              <Statistic
                  title={<span style={{ color: 'var(--lcolor)' }}>Transactions</span>}
                value={todaysStats.transactionCount}
                  prefix={<ShoppingOutlined style={{ color: 'var(--primary-color)' }} />}
                  valueStyle={{ color: 'var(--lcolor)', fontSize: '24px' }}
              />
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Card 
                size="small"
                style={{ 
                  background: 'var(--surface-elevated)',
                  border: '1px solid var(--border-tertiary)',
                  boxShadow: 'var(--shadow-light)'
                }}
              >
                <div>
                  <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '8px' }}>
                    Total Sales
                  </div>
                  {Object.keys(todaysStats.totalsByCurrency || {}).length > 1 ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                      {Object.entries(todaysStats.totalsByCurrency)
                        .sort((a, b) => b[1] - a[1]) // Sort by amount descending
                        .map(([currency, amount]) => (
                          <div 
                            key={currency}
                            style={{ 
                              display: 'flex', 
                              alignItems: 'center',
                              padding: '6px 10px',
                              background: 'var(--surface-tertiary)',
                              borderRadius: 'var(--radius-sm)',
                              border: '1px solid var(--border-tertiary)'
                            }}
                          >
                            <DollarOutlined style={{ marginRight: '6px', color: 'var(--primary-color)', fontSize: '14px' }} />
                            <span style={{ fontSize: '16px', fontWeight: 600, color: 'var(--lcolor)' }}>
                              {formatPriceWithCurrency(amount, currency, facilityId)}
                            </span>
                          </div>
                        ))}
                    </div>
                  ) : (
                    <div style={{ fontSize: '24px', fontWeight: 600, color: 'var(--lcolor)' }}>
                      <DollarOutlined style={{ marginRight: '8px', color: 'var(--primary-color)' }} />
                      {Object.keys(todaysStats.totalsByCurrency || {}).length > 0
                        ? Object.entries(todaysStats.totalsByCurrency)
                            .map(([currency, amount]) => formatPriceWithCurrency(amount, currency, facilityId))
                            .join(' | ')
                        : formatPriceWithCurrency(todaysStats.totalSales, facilityCurrency, facilityId)}
                    </div>
                  )}
                </div>
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Card 
                size="small"
                style={{ 
                  background: 'var(--surface-elevated)',
                  border: '1px solid var(--border-tertiary)',
                  boxShadow: 'var(--shadow-light)'
                }}
              >
              <Statistic
                  title={<span style={{ color: 'var(--lcolor)' }}>Items Sold</span>}
                value={todaysStats.totalItems}
                  valueStyle={{ color: 'var(--lcolor)', fontSize: '24px' }}
              />
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Card 
                size="small"
                style={{ 
                  background: 'var(--surface-elevated)',
                  border: '1px solid var(--border-tertiary)',
                  boxShadow: 'var(--shadow-light)'
                }}
              >
                <div>
                  <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '8px' }}>
                    Total Profit
                  </div>
                  {Object.keys(todaysStats.profitsByCurrency || {}).length > 1 ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                      {Object.entries(todaysStats.profitsByCurrency)
                        .filter(([_, profit]) => profit > 0) // Only show currencies with profit
                        .sort((a, b) => b[1] - a[1]) // Sort by profit descending
                        .map(([currency, profit]) => (
                          <div 
                            key={currency}
                            style={{ 
                              display: 'flex', 
                              alignItems: 'center',
                              padding: '6px 10px',
                              background: 'var(--surface-tertiary)',
                              borderRadius: 'var(--radius-sm)',
                              border: '1px solid var(--success-color)',
                              borderWidth: '1px',
                              borderStyle: 'solid'
                            }}
                          >
                            <span style={{ fontSize: '16px', fontWeight: 600, color: 'var(--success-color)' }}>
                              {formatPriceWithCurrency(profit, currency, facilityId)}
                            </span>
                          </div>
                        ))}
                    </div>
                  ) : (
                    <div style={{ fontSize: '24px', fontWeight: 600, color: 'var(--success-color)' }}>
                      {Object.keys(todaysStats.profitsByCurrency || {}).length > 0
                        ? Object.entries(todaysStats.profitsByCurrency)
                            .filter(([_, profit]) => profit > 0)
                            .map(([currency, profit]) => formatPriceWithCurrency(profit, currency, facilityId))
                            .join(' | ') || formatPriceWithCurrency(todaysStats.totalProfit, facilityCurrency, facilityId)
                        : formatPriceWithCurrency(todaysStats.totalProfit, facilityCurrency, facilityId)}
                    </div>
                  )}
                </div>
              </Card>
            </Col>
          </Row>
        </Card>
      )}

      {/* Today's Transactions */}
      <Card 
        title={`Today's Transactions (${formatDate(new Date().toISOString().split('T')[0])})`}
        style={{ marginBottom: 'var(--spacing-lg)' }}
      >
        {Object.keys(todaysTransactions).length === 0 ? (
          <Empty description="No transactions today" />
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <Table
              columns={transactionColumns}
              dataSource={Object.values(todaysTransactions)
                .map((transaction, index) => ({
                  ...transaction,
                  key: transaction.id || transaction.transactionId || index
                }))
                .sort((a, b) => {
                  const parse = (t) => {
                    if (!t) return 0;
                    let d = new Date(t);
                    if (isNaN(d.getTime())) {
                      const num = Number(t);
                      if (!Number.isNaN(num)) d = new Date(num);
                    }
                    return d.getTime() || 0;
                  };
                  return parse(b.timestamp) - parse(a.timestamp);
                })}
              rowKey="key"
              pagination={{ pageSize: 10 }}
              size="small"
              loading={loading}
              scroll={{ x: 'max-content' }}
            />
          </div>
        )}
      </Card>

      {/* Historical Transactions */}
      {Object.keys(allHistoricalTransactions).length > 0 && (
        <Card 
          title={
            <Space>
              <FolderOutlined style={{ fontSize: '20px', color: 'var(--primary-color)' }} />
              <span style={{ fontSize: '20px', fontWeight: 600, color: 'var(--lcolor)' }}>Transaction History</span>
            </Space>
          }
          extra={
            <Space wrap>
              <Select
                value={periodFilter}
                onChange={(value) => {
                  setPeriodFilter(value);
                  setSelectedPeriod(null);
                  setDateRange(null);
                }}
                style={{ width: 130 }}
                size="small"
              >
                <Select.Option value="all">All Time</Select.Option>
                <Select.Option value="range">Date Range</Select.Option>
                <Select.Option value="year">Year</Select.Option>
                <Select.Option value="month">Month</Select.Option>
                <Select.Option value="week">Week</Select.Option>
                <Select.Option value="day">Day</Select.Option>
              </Select>
              {periodFilter === 'range' && (
                <DatePicker.RangePicker
                  value={dateRange ? [dayjs(dateRange[0]), dayjs(dateRange[1])] : null}
                  onChange={(dates) => {
                    if (dates && dates.length === 2) {
                      setDateRange([dates[0].format('YYYY-MM-DD'), dates[1].format('YYYY-MM-DD')]);
                    } else {
                      setDateRange(null);
                    }
                  }}
                  size="small"
                  style={{ width: 240 }}
                />
              )}
              {(periodFilter === 'year' || periodFilter === 'month' || periodFilter === 'day') && (
                <DatePicker
                  picker={periodFilter === 'year' ? 'year' : periodFilter === 'month' ? 'month' : 'date'}
                  value={selectedPeriod ? dayjs(selectedPeriod) : null}
                  onChange={(date, dateString) => {
                    if (periodFilter === 'year') {
                      setSelectedPeriod(dateString ? `${dateString}-01-01` : null);
                    } else if (periodFilter === 'month') {
                      setSelectedPeriod(dateString ? `${dateString}-01` : null);
                    } else {
                      setSelectedPeriod(dateString || null);
                    }
                  }}
                  size="small"
                  style={{ width: 150 }}
                />
              )}
            <Radio.Group 
              value={viewMode} 
              onChange={(e) => setViewMode(e.target.value)}
              size="small"
            >
              <Radio.Button value="table">Table</Radio.Button>
              <Radio.Button value="cards">Cards</Radio.Button>
            </Radio.Group>
            </Space>
          }
          style={{ 
            marginTop: 'var(--spacing-lg)',
            boxShadow: 'var(--shadow-card)',
            borderRadius: 'var(--radius-lg)'
          }}
        >
          {/* Overall Summary Section */}
          <Card 
            type="inner" 
            title={
              <span style={{ fontSize: '16px', fontWeight: 600, color: 'var(--lcolor)' }}>
                Overall Summary
              </span>
            }
            style={{ 
              marginBottom: 'var(--spacing-lg)',
              background: 'linear-gradient(135deg, var(--surface-primary) 0%, var(--surface-secondary) 100%)',
              border: '1px solid var(--border-tertiary)'
            }}
          >
            <Row gutter={[16, 16]}>
              <Col xs={24} sm={12} lg={6}>
                <Card 
                  size="small"
                  style={{ 
                    background: 'var(--surface-elevated)',
                    border: '1px solid var(--border-tertiary)',
                    boxShadow: 'var(--shadow-light)'
                  }}
                >
                  <Statistic
                    title={<span style={{ color: 'var(--lcolor)' }}>Total Transactions</span>}
                    value={overallStats.totalTransactions}
                    prefix={<ShoppingOutlined style={{ color: 'var(--primary-color)' }} />}
                    valueStyle={{ color: 'var(--lcolor)', fontSize: '24px' }}
                  />
                </Card>
              </Col>
              <Col xs={24} sm={12} lg={6}>
                <Card 
                  size="small"
                  style={{ 
                    background: 'var(--surface-elevated)',
                    border: '1px solid var(--border-tertiary)',
                    boxShadow: 'var(--shadow-light)'
                  }}
                >
                  <div>
                    <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '8px' }}>
                      Total Sales
                    </div>
                    {Object.keys(overallStats.totalsByCurrency || {}).length > 1 ? (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                        {Object.entries(overallStats.totalsByCurrency)
                          .sort((a, b) => b[1] - a[1]) // Sort by amount descending
                          .map(([currency, amount]) => (
                            <div 
                              key={currency}
                              style={{ 
                                display: 'flex', 
                                alignItems: 'center',
                                padding: '6px 10px',
                                background: 'var(--surface-tertiary)',
                                borderRadius: 'var(--radius-sm)',
                                border: '1px solid var(--border-tertiary)'
                              }}
                            >
                              <DollarOutlined style={{ marginRight: '6px', color: 'var(--primary-color)', fontSize: '14px' }} />
                              <span style={{ fontSize: '16px', fontWeight: 600, color: 'var(--lcolor)' }}>
                                {formatPriceWithCurrency(amount, currency, facilityId)}
                              </span>
                            </div>
                          ))}
                      </div>
                    ) : (
                      <div style={{ fontSize: '24px', fontWeight: 600, color: 'var(--lcolor)' }}>
                        <DollarOutlined style={{ marginRight: '8px', color: 'var(--primary-color)' }} />
                        {Object.keys(overallStats.totalsByCurrency || {}).length > 0
                          ? Object.entries(overallStats.totalsByCurrency)
                              .map(([currency, amount]) => formatPriceWithCurrency(amount, currency, facilityId))
                              .join(' | ')
                          : formatPriceWithCurrency(overallStats.totalSales, facilityCurrency, facilityId)}
                      </div>
                    )}
                  </div>
                </Card>
              </Col>
              <Col xs={24} sm={12} lg={6}>
                <Card 
                  size="small"
                  style={{ 
                    background: 'var(--surface-elevated)',
                    border: '1px solid var(--border-tertiary)',
                    boxShadow: 'var(--shadow-light)'
                  }}
                >
                  <Statistic
                    title={<span style={{ color: 'var(--lcolor)' }}>Total Items Sold</span>}
                    value={overallStats.totalItems}
                    valueStyle={{ color: 'var(--lcolor)', fontSize: '24px' }}
                  />
                </Card>
              </Col>
              <Col xs={24} sm={12} lg={6}>
                <Card 
                  size="small"
                  style={{ 
                    background: 'var(--surface-elevated)',
                    border: '1px solid var(--border-tertiary)',
                    boxShadow: 'var(--shadow-light)'
                  }}
                >
                  <div>
                    <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '8px' }}>
                      Total Profit
                    </div>
                    {Object.keys(overallStats.profitsByCurrency || {}).length > 1 ? (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                        {Object.entries(overallStats.profitsByCurrency)
                          .filter(([_, profit]) => profit > 0) // Only show currencies with profit
                          .sort((a, b) => b[1] - a[1]) // Sort by profit descending
                          .map(([currency, profit]) => (
                            <div 
                              key={currency}
                              style={{ 
                                display: 'flex', 
                                alignItems: 'center',
                                padding: '6px 10px',
                                background: 'var(--surface-tertiary)',
                                borderRadius: 'var(--radius-sm)',
                                border: '1px solid var(--success-color)',
                                borderWidth: '1px',
                                borderStyle: 'solid'
                              }}
                            >
                              <span style={{ fontSize: '16px', fontWeight: 600, color: 'var(--success-color)' }}>
                                {formatPriceWithCurrency(profit, currency, facilityId)}
                              </span>
                            </div>
                          ))}
                      </div>
                    ) : (
                      <div style={{ fontSize: '24px', fontWeight: 600, color: 'var(--success-color)' }}>
                        {Object.keys(overallStats.profitsByCurrency || {}).length > 0
                          ? Object.entries(overallStats.profitsByCurrency)
                              .filter(([_, profit]) => profit > 0)
                              .map(([currency, profit]) => formatPriceWithCurrency(profit, currency, facilityId))
                              .join(' | ') || formatPriceWithCurrency(overallStats.totalProfit, facilityCurrency, facilityId)
                          : formatPriceWithCurrency(overallStats.totalProfit, facilityCurrency, facilityId)}
                      </div>
                    )}
                  </div>
                </Card>
              </Col>
            </Row>
          </Card>

          {/* Charts Section */}
          {chartData.length > 0 && (
            <Card 
              type="inner"
              title={
                <Space>
                  <span style={{ fontSize: '16px', fontWeight: 600, color: 'var(--lcolor)' }}>
                    Performance Charts
                  </span>
                </Space>
              }
              extra={
                <Space>
                  <Select
                    value={selectedChartCurrency || facilityCurrency}
                    onChange={(value) => setSelectedChartCurrency(value)}
                    size="small"
                    style={{ width: 120 }}
                    placeholder="Currency"
                  >
                    {availableCurrencies.map(currency => (
                      <Select.Option key={currency} value={currency}>
                        {currency} {currency === facilityCurrency ? '(Default)' : ''}
                      </Select.Option>
                    ))}
                  </Select>
                  <Select
                    value={chartView}
                    onChange={setChartView}
                    size="small"
                    style={{ width: 180 }}
                  >
                    <Select.Option value="separate">Separate Charts</Select.Option>
                    <Select.Option value="sales-profit">Sales & Profit</Select.Option>
                    <Select.Option value="transactions-items">Transactions & Items</Select.Option>
                  </Select>
                </Space>
              }
              style={{ 
                marginBottom: 'var(--spacing-lg)',
                background: 'var(--surface-primary)',
                border: '1px solid var(--border-tertiary)'
              }}
            >
              {chartView === 'sales-profit' ? (
                <Card 
                  size="small"
                  title={<span style={{ color: 'var(--lcolor)', fontSize: '14px' }}>Sales & Profit Trend</span>}
                  style={{ background: 'var(--surface-elevated)', marginBottom: '16px' }}
                >
                  <ResponsiveContainer width="100%" height={300}>
                    <AreaChart data={chartData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="var(--border-tertiary)" />
                      <XAxis 
                        dataKey="date" 
                        tick={{ fill: 'var(--lcolor)', fontSize: 11 }}
                        angle={-45}
                        textAnchor="end"
                        height={60}
                      />
                      <YAxis 
                        yAxisId="left"
                        tick={{ fill: 'var(--lcolor)', fontSize: 11 }}
                        tickFormatter={(value) => formatPriceWithCurrency(value, selectedChartCurrency || facilityCurrency, facilityId)}
                      />
                      <Tooltip 
                        contentStyle={{ 
                          background: 'var(--surface-elevated)', 
                          border: '1px solid var(--border-tertiary)',
                          color: 'var(--lcolor)'
                        }}
                        formatter={(value, name) => {
                          if (name === 'sales' || name === 'profit') {
                            return formatPriceWithCurrency(value, selectedChartCurrency || facilityCurrency, facilityId);
                          }
                          return value;
                        }}
                        labelFormatter={(label) => `Date: ${label}`}
                      />
                      <Legend 
                        wrapperStyle={{ color: 'var(--lcolor)' }}
                        iconType="line"
                      />
                      <Area 
                        type="monotone" 
                        dataKey="sales" 
                        name="Sales"
                        stroke="var(--primary-color)" 
                        fill="var(--primary-color)" 
                        fillOpacity={0.3}
                        yAxisId="left"
                      />
                      <Area 
                        type="monotone" 
                        dataKey="profit" 
                        name="Profit"
                        stroke="var(--success-color)" 
                        fill="var(--success-color)" 
                        fillOpacity={0.3}
                        yAxisId="left"
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </Card>
              ) : chartView === 'transactions-items' ? (
                <Card 
                  size="small"
                  title={<span style={{ color: 'var(--lcolor)', fontSize: '14px' }}>Transactions & Items Sold</span>}
                  style={{ background: 'var(--surface-elevated)', marginBottom: '16px' }}
                >
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={chartData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="var(--border-tertiary)" />
                      <XAxis 
                        dataKey="date" 
                        tick={{ fill: 'var(--lcolor)', fontSize: 11 }}
                        angle={-45}
                        textAnchor="end"
                        height={60}
                      />
                      <YAxis 
                        yAxisId="left"
                        tick={{ fill: 'var(--lcolor)', fontSize: 11 }}
                      />
                      <YAxis 
                        yAxisId="right"
                        orientation="right"
                        tick={{ fill: 'var(--lcolor)', fontSize: 11 }}
                      />
                      <Tooltip 
                        contentStyle={{ 
                          background: 'var(--surface-elevated)', 
                          border: '1px solid var(--border-tertiary)',
                          color: 'var(--lcolor)'
                        }}
                        labelFormatter={(label) => `Date: ${label}`}
                      />
                      <Legend 
                        wrapperStyle={{ color: 'var(--lcolor)' }}
                      />
                      <Bar 
                        yAxisId="left"
                        dataKey="transactions" 
                        name="Transactions"
                        fill="var(--primary-color)" 
                      />
                      <Bar 
                        yAxisId="right"
                        dataKey="items" 
                        name="Items Sold"
                        fill="var(--warning-color)" 
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </Card>
              ) : (
                <Row gutter={[16, 16]}>
                  <Col xs={24} lg={12}>
                    <Card 
                      size="small"
                      title={<span style={{ color: 'var(--lcolor)', fontSize: '14px' }}>Sales Trend</span>}
                      style={{ background: 'var(--surface-elevated)' }}
                    >
                      <ResponsiveContainer width="100%" height={250}>
                        <AreaChart data={chartData}>
                          <CartesianGrid strokeDasharray="3 3" stroke="var(--border-tertiary)" />
                          <XAxis 
                            dataKey="date" 
                            tick={{ fill: 'var(--lcolor)', fontSize: 11 }}
                            angle={-45}
                            textAnchor="end"
                            height={60}
                          />
                          <YAxis 
                            tick={{ fill: 'var(--lcolor)', fontSize: 11 }}
                            tickFormatter={(value) => formatPriceWithCurrency(value, selectedChartCurrency || facilityCurrency, facilityId)}
                          />
                          <Tooltip 
                            contentStyle={{ 
                              background: 'var(--surface-elevated)', 
                              border: '1px solid var(--border-tertiary)',
                              color: 'var(--lcolor)'
                            }}
                            formatter={(value) => formatPriceWithCurrency(value, selectedChartCurrency || facilityCurrency, facilityId)}
                          />
                          <Area 
                            type="monotone" 
                            dataKey="sales" 
                            stroke="var(--primary-color)" 
                            fill="var(--primary-color)" 
                            fillOpacity={0.3}
                          />
                        </AreaChart>
                      </ResponsiveContainer>
                    </Card>
                  </Col>
                  <Col xs={24} lg={12}>
                    <Card 
                      size="small"
                      title={<span style={{ color: 'var(--lcolor)', fontSize: '14px' }}>Profit Trend</span>}
                      style={{ background: 'var(--surface-elevated)' }}
                    >
                      <ResponsiveContainer width="100%" height={250}>
                        <AreaChart data={chartData}>
                          <CartesianGrid strokeDasharray="3 3" stroke="var(--border-tertiary)" />
                          <XAxis 
                            dataKey="date" 
                            tick={{ fill: 'var(--lcolor)', fontSize: 11 }}
                            angle={-45}
                            textAnchor="end"
                            height={60}
                          />
                          <YAxis 
                            tick={{ fill: 'var(--lcolor)', fontSize: 11 }}
                            tickFormatter={(value) => formatPriceWithCurrency(value, selectedChartCurrency || facilityCurrency, facilityId)}
                          />
                          <Tooltip 
                            contentStyle={{ 
                              background: 'var(--surface-elevated)', 
                              border: '1px solid var(--border-tertiary)',
                              color: 'var(--lcolor)'
                            }}
                            formatter={(value) => formatPriceWithCurrency(value, selectedChartCurrency || facilityCurrency, facilityId)}
                          />
                          <Area 
                            type="monotone" 
                            dataKey="profit" 
                            stroke="var(--success-color)" 
                            fill="var(--success-color)" 
                            fillOpacity={0.3}
                          />
                        </AreaChart>
                      </ResponsiveContainer>
                    </Card>
                  </Col>
                  <Col xs={24} lg={12}>
                    <Card 
                      size="small"
                      title={<span style={{ color: 'var(--lcolor)', fontSize: '14px' }}>Transactions Count</span>}
                      style={{ background: 'var(--surface-elevated)' }}
                    >
                      <ResponsiveContainer width="100%" height={250}>
                        <BarChart data={chartData}>
                          <CartesianGrid strokeDasharray="3 3" stroke="var(--border-tertiary)" />
                          <XAxis 
                            dataKey="date" 
                            tick={{ fill: 'var(--lcolor)', fontSize: 11 }}
                            angle={-45}
                            textAnchor="end"
                            height={60}
                          />
                          <YAxis tick={{ fill: 'var(--lcolor)', fontSize: 11 }} />
                          <Tooltip 
                            contentStyle={{ 
                              background: 'var(--surface-elevated)', 
                              border: '1px solid var(--border-tertiary)',
                              color: 'var(--lcolor)'
                            }}
                          />
                          <Bar dataKey="transactions" fill="var(--primary-color)" />
                        </BarChart>
                      </ResponsiveContainer>
                    </Card>
                  </Col>
                  <Col xs={24} lg={12}>
                    <Card 
                      size="small"
                      title={<span style={{ color: 'var(--lcolor)', fontSize: '14px' }}>Items Sold</span>}
                      style={{ background: 'var(--surface-elevated)' }}
                    >
                      <ResponsiveContainer width="100%" height={250}>
                        <BarChart data={chartData}>
                          <CartesianGrid strokeDasharray="3 3" stroke="var(--border-tertiary)" />
                          <XAxis 
                            dataKey="date" 
                            tick={{ fill: 'var(--lcolor)', fontSize: 11 }}
                            angle={-45}
                            textAnchor="end"
                            height={60}
                          />
                          <YAxis tick={{ fill: 'var(--lcolor)', fontSize: 11 }} />
                          <Tooltip 
                            contentStyle={{ 
                              background: 'var(--surface-elevated)', 
                              border: '1px solid var(--border-tertiary)',
                              color: 'var(--lcolor)'
                            }}
                          />
                          <Bar dataKey="items" fill="var(--warning-color)" />
                        </BarChart>
                      </ResponsiveContainer>
                    </Card>
                  </Col>
                </Row>
              )}
            </Card>
          )}

          <Divider style={{ margin: 'var(--spacing-lg) 0' }} />
          {viewMode === 'table' ? (
            <Collapse>
              {Object.keys(filteredHistoricalTransactions)
                .sort((a, b) => new Date(b) - new Date(a))
                .map((date) => {
                  const dayTransactions = filteredHistoricalTransactions[date];
                  const dayStats = calculateDayStats(dayTransactions);
                  
                  return (
                    <Panel
                      header={
                        <Space>
                          <CalendarOutlined style={{color: 'var(--lcolor)'}} />
                          <Text strong style={{color: 'var(--lcolor)'}} >{formatDate(date)}</Text>
                          <Tag color="blue">{dayStats.transactionCount} transactions</Tag>
                          <Tag color="green">
                            {Object.keys(dayStats.totalsByCurrency || {}).length > 0
                              ? Object.entries(dayStats.totalsByCurrency)
                                  .map(([currency, amount]) => formatPriceWithCurrency(amount, currency, facilityId))
                                  .join(' | ')
                              : formatPriceWithCurrency(dayStats.totalSales, facilityCurrency, facilityId)}
                          </Tag>
                        </Space>
                      }
                      key={date}
                    >
                      <div style={{ overflowX: 'auto' }}>
                        <Table
                          columns={transactionColumns}
                          dataSource={Object.values(dayTransactions)
                            .map((transaction, index) => ({
                              ...transaction,
                              key: transaction.id || transaction.transactionId || index
                            }))
                            .sort((a, b) => {
                              const parse = (t) => {
                                if (!t) return 0;
                                let d = new Date(t);
                                if (isNaN(d.getTime())) {
                                  const num = Number(t);
                                  if (!Number.isNaN(num)) d = new Date(num);
                                }
                                return d.getTime() || 0;
                              };
                              return parse(b.timestamp) - parse(a.timestamp);
                            })}
                          rowKey="key"
                          pagination={{ pageSize: 5 }}
                          size="small"
                          scroll={{ x: 'max-content' }}
                        />
                      </div>
                    </Panel>
                  );
                })}
            </Collapse>
          ) : (
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', 
              gap: 'var(--spacing-md)' 
            }}>
              {Object.keys(filteredHistoricalTransactions)
                .sort((a, b) => new Date(b) - new Date(a))
                .map((date) => {
                  const dayTransactions = filteredHistoricalTransactions[date];
                  const dayStats = calculateDayStats(dayTransactions);
                  
                  return (
                    <Card
                      key={date}
                      size="small"
                      title={
                        <Space>
                          <FolderOutlined />
                          {formatDate(date)}
                        </Space>
                      }
                    >
                      <Row gutter={[8, 8]}>
                        <Col span={12}>
                          <Statistic
                            title="Transactions"
                            value={dayStats.transactionCount}
                            valueStyle={{ fontSize: '16px' }}
                          />
                        </Col>
                        <Col span={12}>
                          <div>
                            <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '4px' }}>Sales</div>
                            <div style={{ fontSize: '16px', fontWeight: 600, color: 'var(--text-primary)' }}>
                              {Object.keys(dayStats.totalsByCurrency || {}).length > 0
                                ? Object.entries(dayStats.totalsByCurrency)
                                    .map(([currency, amount]) => formatPriceWithCurrency(amount, currency, facilityId))
                                    .join(' | ')
                                : formatPriceWithCurrency(dayStats.totalSales, facilityCurrency, facilityId)}
                            </div>
                          </div>
                        </Col>
                        <Col span={12}>
                          <Statistic
                            title="Items"
                            value={dayStats.totalItems}
                            valueStyle={{ fontSize: '16px' }}
                          />
                        </Col>
                        <Col span={12}>
                          <Statistic
                            title="Profit"
                            value={dayStats.totalProfit}
                            formatter={(value) => formatPriceWithCurrency(value, facilityCurrency, facilityId)}
                            valueStyle={{ fontSize: '16px', color: 'var(--success-color)' }}
                          />
                        </Col>
                      </Row>
                    </Card>
                  );
                })}
            </div>
          )}
        </Card>
      )}
    </div>
  );
};

export default TransactionHistory;
